import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { t } = useTranslation();
  
  // Hide navigation when inside presence workspace
  const isInPresenceWorkspace = location.pathname.includes('/presence/');

  // Public navigation
  const publicNavLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/technology', label: t('nav.technology') },
    { path: '/features', label: t('nav.features') },
    { path: '/pricing', label: t('nav.pricing') },
    { path: '/ethics', label: t('nav.ethics') }
  ];

  // Logged-in product navigation
  const productNavLinks = [
    { path: '/presences', label: t('nav.myPresences') },
    { path: '/create', label: t('nav.create') }
  ];

  const navLinks = isAuthenticated ? productNavLinks : publicNavLinks;

  const isActive = (path) => location.pathname === path;
  
  // Don't render navigation in presence workspace
  if (isInPresenceWorkspace) {
    return null;
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-cyan-500/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="https://customer-assets.emergentagent.com/job_layout-kit-1/artifacts/fbm0hm7y_Represenx_logo_trans.png" 
              alt="RepresenX" 
              className="h-20 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-cyan-400'
                    : 'text-gray-300 hover:text-cyan-400'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => { logout(); navigate('/'); }}
                  className="text-gray-300 hover:text-red-400 hover:bg-red-500/10"
                  title={t('nav.logout')}
                >
                  <LogOut size={18} />
                </Button>
              </>
            ) : (
              <Link to="/auth">
                <Button className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white">
                  {t('landing.getStarted')}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-300 hover:text-cyan-400"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-6 space-y-4 border-t border-cyan-500/10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-cyan-400'
                    : 'text-gray-300 hover:text-cyan-400'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 space-y-3">
              <div className="flex justify-center pb-3">
                <LanguageSwitcher />
              </div>
              {isAuthenticated ? (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => { logout(); navigate('/'); setMobileMenuOpen(false); }}
                    className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    {t('nav.logout')}
                  </Button>
                </>
              ) : (
                <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 text-white">
                    {t('landing.getStarted')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
